<template>
  <div class="flows-layout">
    <!-- Sidebar -->
    <Sidebar :isAgent="isAgent" />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="custom-flows-view">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-title-section">
          <h1 class="page-title">WhatsApp Custom Flows</h1>
          <p class="page-subtitle">Create and manage custom conversation flows with API integrations</p>
        </div>
        <div class="header-actions">
          <button @click="openImportModal" class="btn-secondary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10" transform="rotate(180 12 12)"/>
              <line x1="12" y1="12" x2="12" y2="21"/>
            </svg>
            Import Flow
          </button>
          <button @click="createNewFlow" class="btn-primary">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="12" y1="5" x2="12" y2="19" stroke-width="2" stroke-linecap="round"/>
              <line x1="5" y1="12" x2="19" y2="12" stroke-width="2" stroke-linecap="round"/>
            </svg>
            Create New Flow
          </button>
        </div>
      </div>
    </div>

    <!-- Custom Flow Mode Status Panel -->
    <div class="mode-panel" :class="{ 'mode-active': customFlowModeEnabled }">
      <div class="mode-icon" :class="{ active: customFlowModeEnabled }">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17,12V3A1,1 0 0,0 16,2H3A1,1 0 0,0 2,3V17L6,13H16A1,1 0 0,0 17,12M21,6H19V15H6V17A1,1 0 0,0 7,18H18L22,22V7A1,1 0 0,0 21,6Z"/>
        </svg>
      </div>
      <div class="mode-info">
        <div class="mode-title">
          Custom Flow Mode
          <span v-if="customFlowModeEnabled" class="mode-live-badge">LIVE</span>
        </div>
        <div class="mode-desc" v-if="customFlowModeEnabled">
          Active flow: <strong>{{ activeFlowName || 'Unknown' }}</strong> — all incoming WhatsApp messages are handled by this flow.
        </div>
        <div class="mode-desc" v-else>
          No flow is currently active. Click <strong>Activate Flow</strong> on any flow below to go live.
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon" style="background: rgba(59, 130, 246, 0.1); color: #3b82f6;">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z M21,16V14H14V16H21M21,11V9H14V11H21Z"/>
          </svg>
        </div>
        <div class="stat-content">
          <div class="stat-label">Total Flows</div>
          <div class="stat-value">{{ totalFlows }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon" style="background: rgba(16, 185, 129, 0.1); color: #10b981;">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        </div>
        <div class="stat-content">
          <div class="stat-label">Active Flows</div>
          <div class="stat-value">{{ activeFlows }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon" style="background: rgba(245, 158, 11, 0.1); color: #f59e0b;">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l3.09 6.26L22 9l-5.09.74L12 16l-4.91-6.26L2 9l6.91-1.74L12 2z"/>
          </svg>
        </div>
        <div class="stat-content">
          <div class="stat-label">Total Sessions</div>
          <div class="stat-value">{{ totalSessions }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon" style="background: rgba(139, 92, 246, 0.1); color: #8b5cf6;">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M13 13v8h8v-8h-8zM3 21h8v-8H3v8zM3 3v8h8V3H3zm13.66-1.31L11 7.34 16.66 13l5.66-5.66-5.66-5.65z"/>
          </svg>
        </div>
        <div class="stat-content">
          <div class="stat-label">Completions</div>
          <div class="stat-value">{{ totalCompletions }}</div>
        </div>
      </div>
    </div>

    <!-- Filters and Search -->
    <div class="filters-bar">
      <div class="search-box">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="11" cy="11" r="8" stroke-width="2"/>
          <path d="M21 21l-4.35-4.35" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search flows..."
          class="search-input"
        />
      </div>

      <div class="filter-buttons">
        <button
          @click="filterStatus = 'all'"
          :class="['filter-btn', { active: filterStatus === 'all' }]"
        >
          All
        </button>
        <button
          @click="filterStatus = 'active'"
          :class="['filter-btn', { active: filterStatus === 'active' }]"
        >
          Active
        </button>
        <button
          @click="filterStatus = 'inactive'"
          :class="['filter-btn', { active: filterStatus === 'inactive' }]"
        >
          Inactive
        </button>
      </div>
    </div>

    <!-- Flows Grid -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading flows...</p>
    </div>

    <div v-else-if="filteredFlows.length === 0" class="empty-state">
      <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" opacity="0.3">
        <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z M21,16V14H14V16H21M21,11V9H14V11H21Z" stroke-width="2"/>
      </svg>
      <h3>No flows found</h3>
      <p>Create your first custom WhatsApp flow to get started</p>
      <button @click="createNewFlow" class="btn-primary">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <line x1="12" y1="5" x2="12" y2="19" stroke-width="2" stroke-linecap="round"/>
          <line x1="5" y1="12" x2="19" y2="12" stroke-width="2" stroke-linecap="round"/>
        </svg>
        Create New Flow
      </button>
    </div>

    <div v-else class="flows-grid">
      <div
        v-for="flow in filteredFlows"
        :key="flow.id"
        class="flow-card"
        :class="{ active: isFlowActive(flow) || isActivatedForUser(flow), inactive: !isFlowActive(flow) && !isActivatedForUser(flow), 'has-target': !!flow.target_owner_id, 'is-older': olderFlowIds.has(flow.id) }"
      >
        <div class="flow-card-header">
          <div class="flow-card-badges">
            <div class="flow-status-badge" :class="{ active: isFlowActive(flow) || isActivatedForUser(flow), inactive: !isFlowActive(flow) && !isActivatedForUser(flow) }">
              {{ isActivatedForUser(flow) ? 'Active for User' : (isFlowActive(flow) ? 'Active' : 'Inactive') }}
            </div>
            <div v-if="flow.target_owner_id" class="flow-target-badge">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
              {{ flow.target_owner_company || flow.target_owner_name }}
            </div>
            <div v-if="olderFlowIds.has(flow.id)" class="flow-older-badge" title="A newer version of this flow exists for the same owner">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
              Older
            </div>
          </div>
          <div class="flow-actions">
            <!-- Admin: activate for a specific user (targeted flows only) -->
            <button
              v-if="isAdmin && flow.target_owner_id && !isActivatedForUser(flow)"
              @click.stop="activateForUser(flow)"
              class="btn-activate-flow btn-activate-user"
              :disabled="activatingFlowId === flow.id"
              :title="`Activate for ${flow.target_owner_company || flow.target_owner_name}`"
            >
              <svg v-if="activatingFlowId === flow.id" width="13" height="13" viewBox="0 0 24 24" fill="currentColor" class="spin"><path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/></svg>
              {{ activatingFlowId === flow.id ? 'Activating...' : 'Activate for User' }}
            </button>
            <!-- Default activate (non-targeted, inactive flows) -->
            <button
              v-if="!isFlowActive(flow) && !flow.target_owner_id"
              @click.stop="activateFlow(flow)"
              class="btn-activate-flow"
              :disabled="activatingFlowId === flow.id"
            >
              <svg v-if="activatingFlowId === flow.id" width="13" height="13" viewBox="0 0 24 24" fill="currentColor" class="spin">
                <path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/>
              </svg>
              {{ activatingFlowId === flow.id ? 'Activating...' : 'Activate Flow' }}
            </button>
            <!-- Admin: duplicate for user button (only on non-targeted flows) -->
            <button
              v-if="isAdmin && !flow.target_owner_id"
              @click.stop="openDuplicateModal(flow)"
              class="icon-btn"
              title="Duplicate for user"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
            <button @click.stop="downloadFlow(flow)" class="icon-btn" title="Download backup">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
            </button>
            <button @click.stop="editFlow(flow.id)" class="icon-btn" title="Edit">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <button
              @click.stop="deleteFlow(flow)"
              class="icon-btn danger"
              :disabled="isFlowActive(flow) || isActivatedForUser(flow)"
              :title="(isFlowActive(flow) || isActivatedForUser(flow)) ? 'Deactivate this flow before deleting' : 'Delete'"
              :style="(isFlowActive(flow) || isActivatedForUser(flow)) ? { opacity: 0.35, cursor: 'not-allowed', pointerEvents: 'none' } : {}"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="3 6 5 6 21 6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="flow-card-content" @click="editFlow(flow.id)">
          <h3 class="flow-name">{{ flow.flow_name }}</h3>
          <p class="flow-description">{{ flow.description || 'No description' }}</p>

          <div class="flow-stats">
            <div class="flow-stat-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2l3.09 6.26L22 9l-5.09.74L12 16l-4.91-6.26L2 9l6.91-1.74L12 2z"/>
              </svg>
              <span>{{ flow.total_sessions || 0 }} sessions</span>
            </div>
            <div class="flow-stat-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span>{{ flow.total_submissions || 0 }} completed</span>
            </div>
          </div>

          <div class="flow-meta">
            <span class="meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke-width="2"/>
                <line x1="16" y1="2" x2="16" y2="6" stroke-width="2" stroke-linecap="round"/>
                <line x1="8" y1="2" x2="8" y2="6" stroke-width="2" stroke-linecap="round"/>
                <line x1="3" y1="10" x2="21" y2="10" stroke-width="2"/>
              </svg>
              {{ formatDate(flow.created_at) }}
            </span>
            <button
              v-if="isFlowActive(flow) && !flow.target_owner_id"
              @click.stop="openStopOwnConfirm(flow)"
              class="btn-deactivate-inline"
              :disabled="stoppingFlowId === flow.id"
            >
              <svg v-if="stoppingFlowId === flow.id" width="11" height="11" viewBox="0 0 24 24" fill="currentColor" class="spin"><path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/></svg>
              <svg v-else width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h12v12H6z"/></svg>
              {{ stoppingFlowId === flow.id ? 'Deactivating...' : 'Deactivate Flow' }}
            </button>
            <button
              v-if="isAdmin && flow.target_owner_id && isActivatedForUser(flow)"
              @click.stop="openStopForUserConfirm(flow)"
              class="btn-deactivate-inline"
              :title="`Deactivate for ${flow.target_owner_company || flow.target_owner_name}`"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h12v12H6z"/></svg>
              Deactivate for User
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Duplicate for User Modal -->
    <div v-if="duplicateModal.show" class="modal-overlay" @click.self="closeDuplicateModal">
      <div class="modal-box">
        <div class="modal-header">
          <h3>Duplicate Flow for User</h3>
          <button @click="closeDuplicateModal" class="modal-close">×</button>
        </div>
        <div class="modal-body">
          <p class="modal-desc">
            This will create a copy of <strong>{{ duplicateModal.flow?.flow_name }}</strong> assigned to the selected user.
            You can then open the copy and adjust department assignments, etc.
          </p>
          <label class="modal-label">Select User</label>
          <select v-model="duplicateModal.selectedUserId" class="modal-select" :disabled="duplicateModal.loading">
            <option :value="null">— choose a user —</option>
            <option v-for="u in assignedUsers" :key="u.owner_id" :value="u.owner_id">
              {{ u.company_name || u.name }} ({{ u.name }})
            </option>
          </select>
          <p v-if="duplicateModal.error" class="modal-error">{{ duplicateModal.error }}</p>
        </div>
        <div class="modal-footer">
          <button @click="closeDuplicateModal" class="btn-cancel">Cancel</button>
          <button
            @click="confirmDuplicate"
            :disabled="!duplicateModal.selectedUserId || duplicateModal.loading"
            class="btn-primary"
          >
            <svg v-if="duplicateModal.loading" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" class="spin"><path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/></svg>
            {{ duplicateModal.loading ? 'Duplicating...' : 'Duplicate & Open' }}
          </button>
        </div>
      </div>
    </div>
    <!-- Deactivate Flow Confirmation Modal (own + for-user) -->
    <div v-if="stopConfirm.show" class="modal-overlay" @click.self="stopConfirm.show = false">
      <div class="modal-box" style="max-width:420px">
        <div class="modal-header">
          <h3>{{ stopConfirm.mode === 'own' ? 'Deactivate Your Flow?' : 'Deactivate Flow for User?' }}</h3>
          <button @click="stopConfirm.show = false" class="modal-close">×</button>
        </div>
        <div class="modal-body">
          <p v-if="stopConfirm.mode === 'own'" class="modal-desc">
            This will deactivate <strong>{{ stopConfirm.flow?.flow_name }}</strong>.
            All incoming WhatsApp messages will no longer be handled by this flow.
          </p>
          <p v-else class="modal-desc">
            This will stop the active flow <strong>{{ stopConfirm.flow?.flow_name }}</strong> for
            <strong>{{ stopConfirm.flow?.target_owner_company || stopConfirm.flow?.target_owner_name }}</strong>.
            Their automation will pause immediately.
          </p>
        </div>
        <div class="modal-footer">
          <button @click="stopConfirm.show = false" class="btn-cancel">Cancel</button>
          <button
            @click="confirmStop"
            class="btn-danger"
            :disabled="stoppingFlowId === stopConfirm.flow?.id"
          >
            <svg v-if="stoppingFlowId === stopConfirm.flow?.id" width="13" height="13" viewBox="0 0 24 24" fill="currentColor" class="spin"><path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/></svg>
            <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h12v12H6z"/></svg>
            {{ stoppingFlowId === stopConfirm.flow?.id ? 'Deactivating...' : 'Yes, Deactivate' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Import Flow Modal -->
    <div v-if="importModal.show" class="modal-overlay" @click.self="closeImportModal">
      <div class="modal-box import-modal-box">
        <div class="modal-header">
          <h3>Import Flow from Backup</h3>
          <button @click="closeImportModal" class="modal-close">×</button>
        </div>
        <div class="modal-body">
          <input ref="importFileInput" type="file" accept=".json" style="display:none" @change="onImportFileChange" />
          <div
            class="import-dropzone"
            :class="{ 'drag-over': importModal.dragOver, 'has-file': !!importModal.parsedData, 'has-error': !!importModal.error && !importModal.parsedData }"
            @dragover.prevent="onImportDragOver"
            @dragleave="onImportDragLeave"
            @drop.prevent="onImportDrop"
            @click="$refs.importFileInput.click()"
          >
            <!-- Default state -->
            <template v-if="!importModal.parsedData && !importModal.error">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="dz-icon">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <p class="dz-title">Drag &amp; drop a backup .json file here</p>
              <p class="dz-sub">or click to browse your files</p>
            </template>

            <!-- Error state -->
            <template v-else-if="importModal.error && !importModal.parsedData">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="1.5" class="dz-icon">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12" stroke-linecap="round"/>
                <circle cx="12" cy="16" r="0.5" fill="#ef4444"/>
              </svg>
              <p class="dz-error-text">{{ importModal.error }}</p>
              <p class="dz-sub">Click to choose a different file</p>
            </template>

            <!-- Valid file preview -->
            <template v-else-if="importModal.parsedData">
              <div class="import-preview">
                <div class="preview-check">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <div class="preview-info">
                  <div class="preview-flow-name">{{ importModal.parsedData.flow_name || 'Unnamed Flow' }}</div>
                  <div class="preview-meta-row">
                    <span class="preview-badge">{{ importModal.nodeCount }} nodes</span>
                    <span v-if="importModal.parsedData.exported_at" class="preview-date">
                      Exported {{ formatDate(importModal.parsedData.exported_at) }}
                    </span>
                  </div>
                  <div v-if="importModal.parsedData.description" class="preview-desc">{{ importModal.parsedData.description }}</div>
                </div>
              </div>
              <p class="dz-sub dz-sub-replace">Click to choose a different file</p>
            </template>
          </div>
          <!-- Admin: assign to user on import -->
          <div v-if="isAdmin" class="import-user-row">
            <label class="modal-label">Import for User <span class="import-user-optional">(optional — leave blank to import for yourself)</span></label>
            <select v-model="importModal.selectedUserId" class="modal-select" :disabled="importModal.importing">
              <option :value="null">— myself (admin) —</option>
              <option v-for="u in assignedUsers" :key="u.owner_id" :value="u.owner_id">
                {{ u.company_name || u.name }} ({{ u.name }})
              </option>
            </select>
          </div>
          <p class="import-note">A new flow entry will be created (disabled by default). You can then open and edit it before activating.</p>
        </div>
        <div class="modal-footer">
          <button @click="closeImportModal" class="btn-cancel">Cancel</button>
          <button
            @click="confirmImport"
            :disabled="!importModal.parsedData || importModal.importing"
            class="btn-primary"
          >
            <svg v-if="importModal.importing" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" class="spin"><path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/></svg>
            {{ importModal.importing ? 'Importing...' : (importModal.selectedUserId ? 'Import for User' : 'Import Flow') }}
          </button>
        </div>
      </div>
    </div>

        <!-- Admin: User Flow Assignments -->
    <div v-if="isAdmin" class="assignments-section">
      <div class="assignments-header" @click="assignmentsExpanded = !assignmentsExpanded">
        <div class="assignments-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
          </svg>
          User Flow Assignments
          <span class="assignments-count">{{ assignedUsers.filter(u => u.custom_flow_mode).length }} / {{ assignedUsers.length }} active</span>
        </div>
        <svg :class="['chevron', { rotated: assignmentsExpanded }]" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      <div v-if="assignmentsExpanded">
        <div v-if="usersLoading" class="assignments-loading">
          <div class="spinner" style="width:24px;height:24px;border-width:3px;margin:0 auto 8px;"></div>
          Loading users...
        </div>

        <div v-else-if="assignedUsers.length === 0" class="assignments-empty">
          No users found.
        </div>

        <table v-else class="assignments-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Company</th>
              <th>Assigned Flow</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in assignedUsers" :key="user.owner_id">
              <td>
                <div class="user-cell">
                  <span :class="['user-dot', user.custom_flow_mode ? 'dot-active' : 'dot-inactive']"></span>
                  {{ user.name }}
                </div>
              </td>
              <td class="td-company">{{ user.company_name || '—' }}</td>
              <td>
                <select
                  v-model="userPendingFlowId[user.owner_id]"
                  class="flow-select"
                >
                  <option :value="null">— No flow —</option>
                  <option v-for="flow in flows" :key="flow.id" :value="flow.id">
                    {{ flow.flow_name }}
                  </option>
                </select>
              </td>
              <td>
                <button
                  @click="saveUserAssignment(user)"
                  :disabled="savingUserId === user.owner_id || !isPendingChanged(user)"
                  :class="['btn-save-assignment', { changed: isPendingChanged(user) }]"
                >
                  <svg v-if="savingUserId === user.owner_id" width="13" height="13" viewBox="0 0 24 24" fill="currentColor" class="spin">
                    <path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/>
                  </svg>
                  {{ savingUserId === user.owner_id ? 'Saving...' : isPendingChanged(user) ? 'Save' : 'Saved' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

        </div>
      </main>
    </div>
  </div>
</template>

<script>
import DashboardService from '../services/dashboard'
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import { authStore } from '../store/auth'

const ADMIN_OWNER_ID = 2

export default {
  name: 'CustomFlowsView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      isAgent: !!localStorage.getItem('agent_token'),
      flows: [],
      loading: true,
      searchQuery: '',
      filterStatus: 'all',
      totalFlows: 0,
      activeFlows: 0,
      totalSessions: 0,
      totalCompletions: 0,
      customFlowModeEnabled: false,
      activeFlowId: null,
      activeFlowName: null,
      activatingFlowId: null,
      stoppingFlowId: null,
      stopConfirm: { show: false, flow: null, mode: 'own' },
      // Admin assignment
      assignedUsers: [],
      usersLoading: false,
      userPendingFlowId: {}, // { [owner_id]: selected flow_id }
      savingUserId: null,
      assignmentsExpanded: true,
      // Duplicate modal
      duplicateModal: {
        show: false,
        flow: null,
        selectedUserId: null,
        loading: false,
        error: ''
      },
      // Import modal
      importModal: {
        show: false,
        dragOver: false,
        parsedData: null,
        nodeCount: 0,
        error: '',
        importing: false
      }
    }
  },

  computed: {
    isAdmin () {
      return authStore.user?.id === ADMIN_OWNER_ID
    },

    // Set of flow IDs that are NOT the newest for their owner group
    olderFlowIds () {
      const groups = {}
      this.flows.forEach(flow => {
        const key = flow.target_owner_id || 'own'
        if (!groups[key]) groups[key] = []
        groups[key].push(flow)
      })
      const olderIds = new Set()
      Object.values(groups).forEach(group => {
        if (group.length <= 1) return
        const sorted = [...group].sort((a, b) => b.id - a.id)
        sorted.slice(1).forEach(f => olderIds.add(f.id))
      })
      return olderIds
    },

    filteredFlows () {
      let filtered = this.flows

      // Filter by status
      if (this.filterStatus === 'active') {
        filtered = filtered.filter(flow => this.isFlowActive(flow) || this.isActivatedForUser(flow))
      } else if (this.filterStatus === 'inactive') {
        filtered = filtered.filter(flow => !this.isFlowActive(flow) && !this.isActivatedForUser(flow))
      }

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(flow =>
          flow.flow_name.toLowerCase().includes(query) ||
          (flow.description && flow.description.toLowerCase().includes(query))
        )
      }

      return filtered
    }
  },

  mounted () {
    this.loadFlows()
    this.checkCustomFlowMode()
    if (this.isAdmin) this.loadAssignedUsers()
  },

  methods: {
    async loadFlows () {
      this.loading = true
      try {
        const response = await DashboardService.getCustomFlows()
        console.log('Custom flows response:', response)

        if (response && response.success && response.flows) {
          this.flows = response.flows
          this.calculateStats()
        } else if (response && response.success && Array.isArray(response.data)) {
          // Handle if the API returns flows in a 'data' property
          this.flows = response.data
          this.calculateStats()
        } else {
          // No flows yet, show empty state
          this.flows = []
          this.calculateStats()
        }
      } catch (error) {
        console.error('Failed to load flows:', error)
        // Show error message using alert if toast is not available
        if (this.$toast) {
          this.$toast.error('Failed to load flows')
        } else {
          console.error('Failed to load flows - toast not available')
        }
        this.flows = []
      } finally {
        this.loading = false
      }
    },

    isFlowActive (flow) {
      if (this.customFlowModeEnabled && this.activeFlowId) {
        // eslint-disable-next-line eqeqeq
        return flow.id == this.activeFlowId
      }
      return !!flow.enabled
    },

    calculateStats () {
      this.totalFlows = this.flows.length
      this.activeFlows = this.flows.filter(f => this.isFlowActive(f)).length
      this.totalSessions = this.flows.reduce((sum, f) => sum + (f.total_sessions || 0), 0)
      this.totalCompletions = this.flows.reduce((sum, f) => sum + (f.total_submissions || 0), 0)
    },

    createNewFlow () {
      this.$router.push('/flow-builder')
    },

    editFlow (flowId) {
      this.$router.push(`/flow-builder/${flowId}`)
    },

    async activateFlow (flow) {
      this.activatingFlowId = flow.id
      try {
        const result = await DashboardService.toggleCustomFlowMode(flow.id)
        if (result.success && result.customFlowMode) {
          this.customFlowModeEnabled = true
          this.activeFlowId = result.activeFlowId
          this.activeFlowName = flow.flow_name
          authStore.setCustomFlowMode(true, result.activeFlowId)
          this.calculateStats()
          if (this.$toast) this.$toast.success(`"${flow.flow_name}" is now the active flow`)
        } else {
          if (this.$toast) this.$toast.error(result.message || 'Failed to activate flow')
        }
      } catch (error) {
        console.error('Failed to activate flow:', error)
        if (this.$toast) this.$toast.error('Failed to activate flow')
      } finally {
        this.activatingFlowId = null
      }
    },

    async deleteFlow (flow) {
      if (this.isFlowActive(flow) || this.isActivatedForUser(flow)) {
        if (this.$toast) this.$toast.error('Deactivate this flow before deleting it.')
        return
      }
      if (!confirm(`Are you sure you want to delete "${flow.flow_name}"? This action cannot be undone.`)) {
        return
      }

      try {
        const response = await DashboardService.deleteCustomFlow(flow.id)
        if (response && response.success) {
          this.flows = this.flows.filter(f => f.id !== flow.id)
          this.calculateStats()
          if (this.$toast) {
            this.$toast.success('Flow deleted successfully')
          }
        }
      } catch (error) {
        console.error('Failed to delete flow:', error)
        if (this.$toast) {
          this.$toast.error('Failed to delete flow')
        }
      }
    },

    async downloadFlow (flow) {
      try {
        const result = await DashboardService.getCustomFlow(flow.id)
        if (!result.success || !result.flow) {
          if (this.$toast) this.$toast.error('Failed to fetch flow for download')
          return
        }
        const backup = {
          flow_name: result.flow.flow_name,
          description: result.flow.description,
          flow_definition: result.flow.flow_definition,
          exported_at: new Date().toISOString()
        }
        const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${flow.flow_name.replace(/[^a-z0-9]/gi, '_')}_backup_${new Date().toISOString().slice(0, 10)}.json`
        a.click()
        URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Failed to download flow:', error)
        if (this.$toast) this.$toast.error('Failed to download flow')
      }
    },

    openImportModal () {
      this.importModal = { show: true, dragOver: false, parsedData: null, nodeCount: 0, error: '', importing: false, selectedUserId: null }
      if (this.isAdmin && !this.assignedUsers.length) this.loadAssignedUsers()
    },

    closeImportModal () {
      this.importModal.show = false
    },

    onImportDragOver () {
      this.importModal.dragOver = true
    },

    onImportDragLeave () {
      this.importModal.dragOver = false
    },

    onImportDrop (e) {
      this.importModal.dragOver = false
      const file = e.dataTransfer.files[0]
      if (file) this.processImportFile(file)
    },

    onImportFileChange (e) {
      const file = e.target.files[0]
      e.target.value = ''
      if (file) this.processImportFile(file)
    },

    async processImportFile (file) {
      this.importModal.parsedData = null
      this.importModal.error = ''
      this.importModal.nodeCount = 0

      if (!file.name.endsWith('.json') && file.type !== 'application/json') {
        this.importModal.error = 'Please select a .json file'
        return
      }

      try {
        const text = await file.text()
        const parsed = JSON.parse(text)

        let backup

        if (parsed.flow_definition) {
          // Standard backup format: { flow_name, description, flow_definition, exported_at }
          backup = parsed
        } else if (parsed.nodes || parsed.start_node || parsed.version) {
          // Raw flow definition file — wrap it so the rest of the logic stays the same
          const nameFromFile = file.name.replace(/\.json$/i, '').replace(/_/g, ' ')
          backup = {
            flow_name: nameFromFile,
            description: '',
            flow_definition: parsed
          }
        } else {
          this.importModal.error = 'Invalid file — not a flow backup or a flow definition'
          return
        }

        // Count nodes
        try {
          let def = backup.flow_definition
          if (typeof def === 'string') def = JSON.parse(def)
          this.importModal.nodeCount = Array.isArray(def?.nodes) ? def.nodes.length : 0
        } catch (_) {
          this.importModal.nodeCount = 0
        }

        this.importModal.parsedData = backup
      } catch (e) {
        this.importModal.error = 'Could not parse file — make sure it is a valid JSON file'
      }
    },

    async confirmImport () {
      if (!this.importModal.parsedData) return
      this.importModal.importing = true

      const backup = this.importModal.parsedData
      const targetUserId = this.importModal.selectedUserId || null

      const flowName = backup.flow_name
        ? `${backup.flow_name} (Imported)`
        : `Imported Flow ${new Date().toLocaleDateString()}`

      const payload = {
        flow_name: flowName,
        description: backup.description || '',
        flow_definition: backup.flow_definition,
        enabled: 0
      }
      if (targetUserId) payload.target_owner_id = targetUserId

      try {
        const result = await DashboardService.createCustomFlow(payload)

        if (result.success) {
          const targetUser = targetUserId ? this.assignedUsers.find(u => u.owner_id === targetUserId) : null
          const forMsg = targetUser ? ` for ${targetUser.company_name || targetUser.name}` : ''
          this.closeImportModal()
          if (this.$toast) this.$toast.success(`Flow "${flowName}" imported${forMsg} successfully`)
          await this.loadFlows()
        } else {
          this.importModal.error = result.message || 'Failed to import flow'
          this.importModal.parsedData = null
        }
      } catch (e) {
        console.error('Import failed:', e)
        this.importModal.error = 'Request failed — please try again'
        this.importModal.parsedData = null
      } finally {
        this.importModal.importing = false
      }
    },

    async checkCustomFlowMode () {
      const result = await DashboardService.checkCustomFlowMode()
      if (result.success) {
        this.customFlowModeEnabled = result.customFlowMode
        this.activeFlowId = result.activeFlowId
        this.activeFlowName = result.activeFlowName
        authStore.setCustomFlowMode(result.customFlowMode, result.activeFlowId)
      }
    },

    async loadAssignedUsers () {
      this.usersLoading = true
      try {
        const result = await DashboardService.adminGetUsers()
        if (result.success) {
          this.assignedUsers = result.users
          // Seed pending selections from current assignments
          const pending = {}
          result.users.forEach(u => {
            pending[u.owner_id] = u.custom_flow_active_id || null
          })
          this.userPendingFlowId = pending
        }
      } catch (e) {
        console.error('Failed to load users', e)
      } finally {
        this.usersLoading = false
      }
    },

    async saveUserAssignment (user) {
      this.savingUserId = user.owner_id
      const flowId = this.userPendingFlowId[user.owner_id] || null
      try {
        const result = await DashboardService.adminAssignFlow(user.owner_id, flowId)
        if (result.success) {
          user.custom_flow_active_id = flowId
          user.custom_flow_mode = !!flowId
          user.active_flow_name = result.active_flow_name || null
          if (this.$toast) this.$toast.success(result.message)
        } else {
          if (this.$toast) this.$toast.error(result.message || 'Failed to update assignment')
        }
      } catch (e) {
        if (this.$toast) this.$toast.error('Request failed')
      } finally {
        this.savingUserId = null
      }
    },

    isPendingChanged (user) {
      const current = user.custom_flow_active_id || null
      const pending = this.userPendingFlowId[user.owner_id] || null
      // eslint-disable-next-line eqeqeq
      return current != pending
    },

    // ── Flow duplication ──────────────────────────────────────────────

    openDuplicateModal (flow) {
      if (!this.assignedUsers.length) this.loadAssignedUsers()
      this.duplicateModal = { show: true, flow, selectedUserId: null, loading: false, error: '' }
    },

    closeDuplicateModal () {
      this.duplicateModal.show = false
    },

    async confirmDuplicate () {
      const { flow, selectedUserId } = this.duplicateModal
      if (!selectedUserId) return
      this.duplicateModal.loading = true
      this.duplicateModal.error = ''
      try {
        const result = await DashboardService.duplicateCustomFlow(flow.id, selectedUserId)
        if (result.success) {
          this.closeDuplicateModal()
          if (this.$toast) this.$toast.success(result.message)
          // Navigate to the new duplicated flow
          this.$router.push(`/flow-builder/${result.flow_id}`)
        } else {
          this.duplicateModal.error = result.message || 'Failed to duplicate flow'
        }
      } catch (e) {
        this.duplicateModal.error = 'Request failed'
      } finally {
        this.duplicateModal.loading = false
      }
    },

    // Returns true if this targeted flow is already activated for its target user
    isActivatedForUser (flow) {
      if (!flow.target_owner_id) return false
      const u = this.assignedUsers.find(u => u.owner_id === flow.target_owner_id)
      if (!u) return false
      // eslint-disable-next-line eqeqeq
      return u.custom_flow_mode && u.custom_flow_active_id == flow.id
    },

    async activateForUser (flow) {
      if (!flow.target_owner_id) return
      this.activatingFlowId = flow.id
      try {
        const result = await DashboardService.adminAssignFlow(flow.target_owner_id, flow.id)
        if (result.success) {
          // Update local assignedUsers record
          const u = this.assignedUsers.find(u => u.owner_id === flow.target_owner_id)
          if (u) {
            u.custom_flow_mode = true
            u.custom_flow_active_id = flow.id
            u.active_flow_name = flow.flow_name
            this.userPendingFlowId[u.owner_id] = flow.id
          }
          if (this.$toast) this.$toast.success(result.message)
        } else {
          if (this.$toast) this.$toast.error(result.message || 'Failed to activate')
        }
      } catch (e) {
        if (this.$toast) this.$toast.error('Request failed')
      } finally {
        this.activatingFlowId = null
      }
    },

    async stopFlow (flow) {
      this.stoppingFlowId = flow.id
      try {
        // Calling toggleCustomFlowMode with no flow_id disables the mode
        const result = await DashboardService.toggleCustomFlowMode(null)
        if (result.success) {
          this.customFlowModeEnabled = false
          this.activeFlowId = null
          this.activeFlowName = null
          authStore.setCustomFlowMode(false, null)
          this.calculateStats()
          if (this.$toast) this.$toast.success(`Flow "${flow.flow_name}" stopped`)
        } else {
          if (this.$toast) this.$toast.error(result.message || 'Failed to stop flow')
        }
      } catch (e) {
        if (this.$toast) this.$toast.error('Request failed')
      } finally {
        this.stoppingFlowId = null
      }
    },

    openStopOwnConfirm (flow) {
      this.stopConfirm = { show: true, flow, mode: 'own' }
    },

    openStopForUserConfirm (flow) {
      this.stopConfirm = { show: true, flow, mode: 'user' }
    },

    async confirmStop () {
      if (this.stopConfirm.mode === 'own') {
        await this.stopFlow(this.stopConfirm.flow)
      } else {
        await this.stopForUser(this.stopConfirm.flow)
      }
      this.stopConfirm.show = false
    },

    async stopForUser (flow) {
      if (!flow.target_owner_id) return
      this.stoppingFlowId = flow.id
      try {
        // adminAssignFlow with null flow_id sets custom_flow_mode = 0 for that user
        const result = await DashboardService.adminAssignFlow(flow.target_owner_id, null)
        if (result.success) {
          const u = this.assignedUsers.find(u => u.owner_id === flow.target_owner_id)
          if (u) {
            u.custom_flow_mode = false
            u.custom_flow_active_id = null
            this.userPendingFlowId[u.owner_id] = null
          }
          if (this.$toast) this.$toast.success(`Flow stopped for ${flow.target_owner_company || flow.target_owner_name}`)
        } else {
          if (this.$toast) this.$toast.error(result.message || 'Failed to stop flow')
        }
      } catch (e) {
        if (this.$toast) this.$toast.error('Request failed')
      } finally {
        this.stoppingFlowId = null
      }
    },

    formatDate (dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
/* Layout Structure */
.flows-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.custom-flows-view {
  background: var(--bg-secondary);
}

/* Custom Flow Mode Status Panel */
.mode-panel {
  display: flex;
  align-items: center;
  gap: 16px;
  background: var(--bg-primary);
  border: 2px solid var(--border-color);
  border-radius: 16px;
  padding: 18px 24px;
  margin-bottom: 24px;
  transition: all 0.3s ease;
}

.mode-panel.mode-active {
  border-color: #10b981;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.03) 100%);
}

.mode-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(100, 116, 139, 0.1);
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.mode-icon.active {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
}

.mode-info {
  flex: 1;
}

.mode-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 3px;
}

.mode-live-badge {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #fff;
  background: #10b981;
  padding: 2px 8px;
  border-radius: 20px;
}

.mode-desc {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.5;
}

.mode-desc strong {
  color: var(--text-primary);
}

.spin {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Page Header */
.page-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.header-title-section {
  flex: 1;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: transparent;
  color: #3b82f6;
  border: 2px solid #3b82f6;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background: rgba(59, 130, 246, 0.08);
  transform: translateY(-2px);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  background: var(--card-background);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 12px;
  color: var(--text-tertiary);
  margin-bottom: 4px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
}

/* Filters Bar */
.filters-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 300px;
  position: relative;
  display: flex;
  align-items: center;
}

.search-box svg {
  position: absolute;
  left: 16px;
  color: var(--text-tertiary);
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 48px;
  background: var(--card-background);
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.filter-buttons {
  display: flex;
  gap: 8px;
  background: var(--card-background);
  border: 1px solid var(--border-primary);
  padding: 4px;
  border-radius: 10px;
}

.filter-btn {
  padding: 8px 16px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-btn:hover {
  background: var(--hover-background);
  color: var(--text-primary);
}

.filter-btn.active {
  background: var(--primary-color);
  color: white;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: var(--text-tertiary);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-primary);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  color: var(--text-tertiary);
}

.empty-state h3 {
  margin: 16px 0 8px;
  font-size: 20px;
  color: var(--text-primary);
}

.empty-state p {
  margin: 0 0 24px;
  font-size: 14px;
}

/* Flows Grid */
.flows-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.flow-card {
  background: var(--card-background);
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s;
  cursor: pointer;
}

.flow-card:hover {
  border-color: var(--primary-color);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.15);
}

.flow-card.active {
  border-color: #10b981;
  box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.3), 0 4px 24px rgba(16, 185, 129, 0.18);
  animation: active-card-pulse 3s ease-in-out infinite;
}

@keyframes active-card-pulse {
  0%, 100% { box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.3), 0 4px 24px rgba(16, 185, 129, 0.18); }
  50%       { box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.5), 0 6px 32px rgba(16, 185, 129, 0.28); }
}

.flow-card.active .flow-card-header {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(5, 150, 105, 0.06));
  border-bottom-color: rgba(16, 185, 129, 0.2);
}

.flow-card.active .flow-status-badge.active::before {
  content: '';
  display: inline-block;
  width: 7px;
  height: 7px;
  background: #10b981;
  border-radius: 50%;
  margin-right: 6px;
  animation: dot-pulse 1.5s ease-in-out infinite;
}

@keyframes dot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.5; transform: scale(0.7); }
}

.flow-card.active .flow-name {
  color: #10b981;
}

.flow-card.inactive .flow-name,
.flow-card.inactive .flow-description,
.flow-card.inactive .flow-stat-item,
.flow-card.inactive .meta-item,
.flow-card.inactive .flow-status-badge {
  opacity: 0.55;
}

.flow-card-header {
  padding: 16px;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--hover-background);
}

.flow-status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.flow-status-badge.active {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.flow-status-badge.inactive {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
}

.flow-actions {
  display: flex;
  gap: 4px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.icon-btn:hover {
  background: var(--hover-background);
  color: var(--text-primary);
  border-color: var(--text-primary);
}

.icon-btn.danger:hover {
  background: #fee;
  color: #ef4444;
  border-color: #ef4444;
}

.btn-activate-flow {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.3);
}

.btn-activate-flow:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(16, 185, 129, 0.4);
}

.btn-activate-flow:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.flow-card-content {
  padding: 20px;
}

.flow-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.flow-description {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.flow-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-primary);
}

.flow-stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}

.flow-stat-item svg {
  color: var(--primary-color);
}

.flow-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 2px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-tertiary);
}

.meta-item svg {
  color: var(--text-tertiary);
}

/* User Assignments Section */
.assignments-section {
  margin-top: 40px;
  background: var(--card-background);
  border: 1px solid var(--border-primary);
  border-radius: 16px;
  overflow: hidden;
}

.assignments-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid var(--border-primary);
  transition: background 0.15s;
}

.assignments-header:hover {
  background: var(--hover-background);
}

.assignments-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

.assignments-count {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  background: var(--hover-background);
  padding: 2px 10px;
  border-radius: 20px;
}

.chevron {
  color: var(--text-secondary);
  transition: transform 0.2s;
}

.chevron.rotated {
  transform: rotate(180deg);
}

.assignments-loading,
.assignments-empty {
  padding: 32px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
}

.assignments-table {
  width: 100%;
  border-collapse: collapse;
}

.assignments-table thead tr {
  background: var(--hover-background);
}

.assignments-table th {
  padding: 12px 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
  text-align: left;
  border-bottom: 1px solid var(--border-primary);
}

.assignments-table td {
  padding: 14px 20px;
  font-size: 13.5px;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-primary);
  vertical-align: middle;
}

.assignments-table tbody tr:last-child td {
  border-bottom: none;
}

.assignments-table tbody tr:hover td {
  background: var(--hover-background);
}

.user-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.user-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot-active  { background: #10b981; }
.dot-inactive { background: #d1d5db; }

.td-company {
  color: var(--text-secondary);
  font-size: 13px;
}

.flow-select {
  width: 100%;
  max-width: 260px;
  padding: 8px 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
  transition: border-color 0.2s;
}

.flow-select:focus {
  outline: none;
  border-color: var(--primary-color);
}

.btn-save-assignment {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 18px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid var(--border-primary);
  background: transparent;
  color: var(--text-secondary);
}

.btn-save-assignment.changed {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border-color: transparent;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.btn-save-assignment.changed:hover:not(:disabled) {
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  transform: translateY(-1px);
}

.btn-save-assignment:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .filters-bar {
    flex-direction: column;
  }

  .search-box {
    min-width: 100%;
  }

  .filter-buttons {
    width: 100%;
    justify-content: space-between;
  }

  .flows-grid {
    grid-template-columns: 1fr;
  }
}

/* Flow card badges row */
.flow-card-badges {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* Target user badge on flow card */
.flow-target-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #5b21b6);
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
}

/* Targeted flow card gets a subtle purple tint on border */
.flow-card.has-target {
  border-color: rgba(124, 58, 237, 0.35);
}

/* Older-version flow card — muted, dashed border */
.flow-card.is-older {
  opacity: 0.72;
  border-style: dashed;
}
.flow-card.is-older:hover {
  opacity: 1;
}

/* "Older" badge */
.flow-older-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 600;
  color: #92400e;
  background: rgba(245, 158, 11, 0.15);
  border: 1px solid rgba(245, 158, 11, 0.35);
  padding: 2px 7px;
  border-radius: 20px;
  white-space: nowrap;
}

/* Activate for user button — purple variant */
.btn-activate-flow.btn-activate-user {
  background: linear-gradient(135deg, #7c3aed, #5b21b6);
  border-color: transparent;
  color: #fff;
}

.btn-activate-flow.btn-activate-user:hover:not(:disabled) {
  background: linear-gradient(135deg, #6d28d9, #4c1d95);
}

.btn-stop-flow {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 8px;
  border: 1.5px solid #ef4444;
  background: transparent;
  color: #ef4444;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.btn-stop-flow:hover:not(:disabled) {
  background: #ef4444;
  color: #fff;
}
.btn-stop-flow:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-deactivate-inline {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 6px;
  border: 1.5px solid rgba(239, 68, 68, 0.45);
  background: transparent;
  color: #ef4444;
  cursor: pointer;
  white-space: nowrap;
  transition: background .15s, border-color .15s;
  flex-shrink: 0;
}
.btn-deactivate-inline:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.08);
  border-color: #ef4444;
}
.btn-deactivate-inline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Duplicate modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  background: var(--bg-card);
  border: 1px solid var(--border-primary, #374151);
  border-radius: 16px;
  width: 420px;
  max-width: 94vw;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--border-primary);
}

.modal-header h3 {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 22px;
  line-height: 1;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0 4px;
}

.modal-close:hover { color: var(--text-primary); }

.modal-body {
  padding: 20px 24px;
}

.modal-desc {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0 0 16px;
  line-height: 1.6;
}

.modal-desc strong { color: var(--text-primary); }

.modal-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}

.modal-select {
  width: 100%;
  padding: 10px 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
}

.modal-error {
  margin-top: 10px;
  font-size: 13px;
  color: #ef4444;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px 20px;
  border-top: 1px solid var(--border-primary);
}

.btn-cancel {
  padding: 9px 18px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
}

.btn-cancel:hover { color: var(--text-primary); border-color: var(--text-secondary); }
.btn-danger {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 18px; border-radius: 8px; font-size: 13px; font-weight: 600;
  background: #ef4444; color: #fff; border: none; cursor: pointer; transition: background .15s;
}
.btn-danger:hover:not(:disabled) { background: #dc2626; }
.btn-danger:disabled { opacity: 0.55; cursor: not-allowed; }

/* Import modal */
.import-modal-box {
  width: 500px;
}

.import-dropzone {
  border: 2px dashed var(--border-primary);
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--bg-secondary);
  user-select: none;
}

.import-dropzone:hover,
.import-dropzone.drag-over {
  border-color: #3b82f6;
  background: rgba(59, 130, 246, 0.05);
}

.import-dropzone.has-file {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.04);
}

.import-dropzone.has-error {
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.04);
}

.dz-icon {
  opacity: 0.4;
  margin: 0 auto 12px;
  display: block;
}

.dz-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 6px;
}

.dz-sub {
  font-size: 12px;
  color: var(--text-secondary);
  margin: 0;
}

.dz-sub-replace {
  margin-top: 10px;
  opacity: 0.7;
}

.dz-error-text {
  font-size: 13px;
  font-weight: 600;
  color: #ef4444;
  margin: 8px 0 6px;
}

/* Preview inside dropzone */
.import-preview {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  text-align: left;
}

.preview-check {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.preview-info {
  flex: 1;
  min-width: 0;
}

.preview-flow-name {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-meta-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.preview-badge {
  font-size: 11px;
  font-weight: 600;
  background: rgba(59, 130, 246, 0.12);
  color: #3b82f6;
  padding: 2px 8px;
  border-radius: 10px;
}

.preview-date {
  font-size: 12px;
  color: var(--text-secondary);
}

.preview-desc {
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.4;
  max-height: 40px;
  overflow: hidden;
}

.import-note {
  font-size: 12px;
  color: var(--text-secondary);
  margin: 12px 0 0;
  line-height: 1.5;
}
.import-user-row {
  margin-top: 14px;
}
.import-user-optional {
  font-weight: 400;
  font-size: 11px;
  color: var(--text-secondary);
  margin-left: 4px;
}
</style>
